import { Node, useReactFlow } from '@xyflow/react';
import { useSetAtom } from 'jotai';
import { Dispatch, memo, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Option, Select } from '@/lib/v2/components';

import HeaderSidePanel from './HeaderSidePanel';

import { atomSidePanelIsOpen } from '@/modules/AutomationModuleNew/atoms/flows';
import { GroupsManageContactData } from '@/modules/AutomationModuleNew/types/flowsTypes';
import { useGroups } from '@/modules/IntegrationsModule/hooks/CreateOrEditIntegration';

type GroupManageContactFormProps = Node<GroupsManageContactData>;

const GroupManageContactForm = (props: GroupManageContactFormProps) => {
  const { data, id } = props;
  const { t } = useTranslation();
  const [addGroupIdsValues, setAddGroupIdsValues] = useState<Option[]>();
  const [removeGroupIdsValues, setRemoveGroupIdsValues] = useState<Option[]>();
  const { groupsOptions, getGroups } = useGroups();
  const { updateNodeData } = useReactFlow();

  const setSidePanelIsOpen = useSetAtom(atomSidePanelIsOpen);

  const handleCloseSidePanel = useCallback(() => {
    setSidePanelIsOpen(false);
  }, [setSidePanelIsOpen]);

  const handleSelectGroupChange = useCallback(
    (fn: Dispatch<SetStateAction<Option[] | undefined>>) => (option: Option | Option[]) => {
      if (!Array.isArray(option)) {
        return;
      }

      fn(option);
    },
    []
  );

  const handleSave = useCallback(() => {
    updateNodeData(id, {
      input: {
        ...data.input,
        addGroupIds: addGroupIdsValues?.map((option) => ({
          value: Number(option.value),
          type: 'literal',
        })),
        removeGroupIds: removeGroupIdsValues?.map((option) => ({
          value: Number(option.value),
          type: 'literal',
        })),
      },
    } as Partial<GroupsManageContactData>);
    handleCloseSidePanel();
  }, [
    addGroupIdsValues,
    data.input,
    handleCloseSidePanel,
    id,
    removeGroupIdsValues,
    updateNodeData,
  ]);

  useEffect(() => {
    const fetchGroups = async () => {
      const addGroupsIds = data.input.addGroupIds
        .map((groupId) => {
          return groupId.value;
        })
        .filter((groupId) => groupId !== undefined);
      const removeGroupsIds = data.input.removeGroupIds
        .map((groupId) => {
          return groupId.value;
        })
        .filter((groupId) => groupId !== undefined);

      const addGroupsValues = await getGroups(addGroupsIds as number[]);
      const removeGroupsValues = await getGroups(removeGroupsIds as number[]);
      setAddGroupIdsValues(addGroupsValues);
      setRemoveGroupIdsValues(removeGroupsValues);
    };

    void fetchGroups();
  }, [data.input.addGroupIds, data.input.removeGroupIds, getGroups]);

  return (
    <div className="flex w-full flex-col">
      <HeaderSidePanel title={t('action')} onCancel={handleCloseSidePanel} onSave={handleSave} />
      <div className="relative flex w-full flex-col gap-4 p-6">
        <Select
          multiSelect
          withSearch
          label={t('GroupManageContactForm.addGroup')}
          name="addGroupIds"
          options={groupsOptions}
          value={addGroupIdsValues}
          onChange={handleSelectGroupChange(setAddGroupIdsValues)}
        />
        <Select
          multiSelect
          withSearch
          label={t('GroupManageContactForm.removeGroup')}
          name="removeGroupIds"
          options={groupsOptions}
          value={removeGroupIdsValues}
          onChange={handleSelectGroupChange(setRemoveGroupIdsValues)}
        />
      </div>
    </div>
  );
};

export default memo(GroupManageContactForm);
